// components/CustomAutocomplete.jsx
import React from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { styled } from "@mui/material/styles";

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  
  // 1. INPUT PRINCIPAL
  "& .MuiOutlinedInput-root": {
    background: "#FFFFFF", 
    borderRadius: 12,
    border: "1px solid rgba(0, 0, 0, 0.3)", 
    padding: "8px 4px",
    transition: "all 0.2s ease-in-out",
    "&:hover": {
      border: "1px solid rgba(0, 0, 0, 0.5)",
      background: "#F5F5F5", 
    },
    "&.Mui-focused": {
      border: "1px solid #5A4E99", 
      boxShadow: "0 0 0 2px rgba(90, 78, 153, 0.25)",
      background: "#FFFFFF", 
    },
  },
  
  // 2. TEXTO DENTRO DEL INPUT
  "& .MuiInputBase-input": {
    color: "#000000",
    fontSize: "0.95rem",
    padding: 0,
    "&::placeholder": {
      opacity: 1,
    },
  },
  
  // 3. LABEL
  "& .MuiFormLabel-root": {
    color: "#000000",
    fontSize: "0.9rem",
    fontWeight: 500,
    background: "#FFFFFF", 
  },
  "& .MuiFormLabel-root.Mui-focused": {
    color: "#5A4E99",
  },
  
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  
  // 4. LISTA DESPLEGABLE (Contenedor del menú)
  "& .MuiAutocomplete-listbox": {
    background: "#FFFFFF", 
    color: "#000000",
  },
  
  // 5. OPCIONES INDIVIDUALES (Ajuste crítico para quitar la transparencia)
  "& .MuiAutocomplete-option": {
    color: "#000000",
    
    // **Ajuste para la opción seleccionada (fondo sólido)**
    "&.Mui-selected": {
      background: "#E0E0E0", // Gris claro sólido (opaco)
      color: "#000000",
    },
    
    // **Ajuste para el estado hover (fondo sólido)**
    "&:hover": {
      background: "#F0F0F0", // Gris muy claro sólido (opaco)
      color: "#000000",
    },
  },
  
  // 6. INDICADORES/ICONOS
  "& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator": {
    color: "rgba(0, 0, 0, 0.7)",
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiInputBase-input": {
    color: "#000000",
    fontSize: "0.95rem",
  },

  "& .MuiInputLabel-root": {
    color: "#000000",
    fontSize: "0.9rem",
    fontWeight: 500,
  },

  "& .MuiInputLabel-root.Mui-focused": {
    color: "#000000",
  },

  "& .MuiInputLabel-shrink": {
    transform: "translate(12px, -6px) scale(0.75)",
    backgroundColor: "#FFFFFF",
    padding: "0 5px",
    color: "#000000",
  },

  "& .MuiInputLabel-shrink.Mui-focused": {
    color: "#000000",
  },
}));

export default function CustomAutocomplete({
  options = [],
  value = null,
  onChange,
  label,
  required = false,
  getOptionLabel,
  multiple = false,
  freeSolo = false,
  returnLabel = false,
  ...props
}) {
  const normalizedOptions = React.useMemo(() => {
    return options.map((opt) => {
      if (opt == null) return opt;
      if (typeof opt === "string" || typeof opt === "number") {
        return { id: opt, label: String(opt) };
      }
      const id = opt.id ?? opt.value ?? opt.key ?? null;
      const labelVal =
        opt.label ?? opt.name ?? (id !== null ? String(id) : "");
      return { ...opt, id, label: labelVal };
    });
  }, [options]);

  const selectedOption = React.useMemo(() => {
    if (value == null) return null;

    if (typeof value === "string" && returnLabel) {
      return normalizedOptions.find((o) => o.label === value) || null;
    }

    if (typeof value === "object") {
      const valId = value.id ?? value.value ?? null;
      if (valId != null) {
        return (
          normalizedOptions.find((o) => String(o.id) === String(valId)) ||
          value
        );
      }
      return (
        normalizedOptions.find(
          (o) => o.label === (value.label ?? value.name)
        ) || value
      );
    }

    return (
      normalizedOptions.find((o) => String(o.id) === String(value)) || null
    );
  }, [value, normalizedOptions, returnLabel]);

  return (
    <StyledAutocomplete
      disablePortal
      options={normalizedOptions}
      value={selectedOption}
      onChange={(e, newValue) => {
        let newVal = null;
        if (newValue != null) {
          if (returnLabel) {
            newVal = newValue.label ?? newValue.name ?? "";
          } else {
            newVal =
              newValue.id ?? (typeof newValue === "object" ? null : newValue);
          }
        }
        if (onChange) onChange(e, newVal);
      }}
      getOptionLabel={getOptionLabel ?? ((option) => option?.label ?? "")}
      isOptionEqualToValue={(option, val) => {
        const oId = option?.id ?? option;
        const vId = val?.id ?? val;

        // 🔑 Ajuste: si usamos returnLabel y el value es string, comparamos con label
        if (typeof val === "string") {
          return option?.label === val;
        }

        return String(oId) === String(vId);
      }}
      multiple={multiple}
      freeSolo={freeSolo}
      renderInput={(params) => (
        <StyledTextField
          {...params}
          label={label}
          required={required}
          variant="outlined"
          fullWidth
        />
      )}
      {...props}
    />
  );
}
